// src/supabase/tenantStamp.ts
//
// Stamps `tenant_id` on every insert and upsert, centrally.
//
// WHY THIS EXISTS
//   Every table in the `pit` schema carries a `tenant_id` column whose default
//   is `coalesce(core.current_tenant(), '<Salesian>')`. That default is the only
//   thing putting a tenant on most rows: an audit on 2026-08-19 found 25 files
//   in src/ performing insert/upsert and only 2 of them mentioning tenant_id.
//
//   So ~23 of 25 write paths depend on a fallback constant. While Salesian was
//   the only school that was invisible. With Marian live it means any write that
//   slips through with an unresolved tenant lands in Salesian's data.
//
//   The platform wants that default dropped, at which point every unstamped
//   write becomes a NOT NULL violation. Stamping here is what makes that safe.
//
// WHY A WRAPPER RATHER THAN 25 EDITS
//   One chokepoint is auditable; 25 call sites are 25 chances to forget, and
//   every new feature adds another. This mirrors the `getDb` proxy the
//   service-role apps in the fleet already use.
//
// SAFETY
//   - Only fills `tenant_id` when it is absent. An explicit value always wins,
//     so nothing that already stamps correctly changes behaviour.
//   - When the tenant cannot be resolved it stamps nothing and the database
//     default applies — exactly today's behaviour. This never makes a write
//     fail that would otherwise have succeeded.
//   - The client is pinned to one schema (`pit`), and every table in it has a
//     `tenant_id` column, so there is no table for which this is invalid.

import type { SupabaseClient } from '@supabase/supabase-js'

/** The tenant of the signed-in user, kept current by the auth listener below. */
let currentTenantId: string | null = null

export function getStampedTenantId(): string | null {
  return currentTenantId
}

/** Read `app_metadata.tenant_id` off a session user, tolerating any shape. */
function tenantFromUser(user: unknown): string | null {
  const meta = (user as { app_metadata?: Record<string, unknown> } | null)?.app_metadata
  const raw = meta?.tenant_id
  return typeof raw === 'string' && raw.length > 0 ? raw : null
}

/** Add tenant_id to one row, or to each row of an array, when it is missing. */
function stamp<T>(values: T): T {
  if (currentTenantId === null || values === null || typeof values !== 'object') {
    return values
  }
  const apply = (row: Record<string, unknown>) =>
    row === null || typeof row !== 'object' || 'tenant_id' in row
      ? row
      : { ...row, tenant_id: currentTenantId }

  return (Array.isArray(values)
    ? values.map((r) => apply(r as Record<string, unknown>))
    : apply(values as Record<string, unknown>)) as T
}

/**
 * Wrap a client so `.from(t).insert(...)` and `.upsert(...)` stamp tenant_id.
 *
 * Mutates the client's `from` in place rather than returning a new object, so
 * every existing `import { supabase }` gets the behaviour without touching a
 * single call site.
 */
export function installTenantStamping(client: SupabaseClient): SupabaseClient {
  // Keep the tenant current. Fires immediately with the restored session on
  // load, and again on sign-in, token refresh and sign-out.
  client.auth.onAuthStateChange((_event, session) => {
    currentTenantId = tenantFromUser(session?.user)
  })
  // onAuthStateChange can land after the first query on a warm cache, so seed it.
  void client.auth.getSession().then(({ data }) => {
    if (currentTenantId === null) currentTenantId = tenantFromUser(data.session?.user)
  })

  const originalFrom = client.from.bind(client)

  client.from = ((table: string) => {
    const builder = originalFrom(table)
    const originalInsert = builder.insert.bind(builder)
    const originalUpsert = builder.upsert.bind(builder)

    builder.insert = ((values: unknown, options?: unknown) =>
      originalInsert(stamp(values) as never, options as never)) as typeof builder.insert

    builder.upsert = ((values: unknown, options?: unknown) =>
      originalUpsert(stamp(values) as never, options as never)) as typeof builder.upsert

    return builder
  }) as typeof client.from

  return client
}
